import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown, Form } from "react-bootstrap";

// Below this many options, the list fits on screen without scrolling -- skip the filter box
// and keep the dropdown as simple as the Yes/No/None case.
const FILTER_THRESHOLD = 8;

// Renders the menu into document.body instead of in place, the same fix used by
// ActionDropdown (src/components/common/ActionDropdown.js) for the same problem: a menu
// opened from a row near the bottom of a scrolling/clipped container (like this grid's
// overflow:auto wrapper) otherwise gets cut off instead of showing all its options.
// Paired with popperConfig's fixed strategy below so Popper still positions it correctly.
// #2636: this menu is portaled to document.body as a sibling of any Bootstrap Modal, not a
// descendant of it -- so without an explicit z-index above the modal's (1055, backdrop at 1050),
// the menu renders underneath the modal's backdrop. It opens and is visible, but every click on
// an option actually lands on the backdrop instead, which looks like the dropdown can't be
// selected at all.
const PortalDropdownMenu = React.forwardRef(
  ({ children, show, close, align, style, ...domProps }, ref) =>
    createPortal(
      <div ref={ref} style={{ ...style, zIndex: 1100 }} {...domProps}>
        {children}
      </div>,
      document.body
    )
);

// Matches the portaled menu's width to the toggle's, since it's no longer a sibling in the
// DOM for CSS alone to size it against.
const sameWidthModifier = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  },
};

// Once a line has a picklist, the value can only be chosen from it -- no free typing. A native
// <select> would enforce that, but its options list is rendered by the OS (Windows/macOS chrome)
// and can't be styled, so this recreates the same "pick one from a locked list" behavior with a
// button trigger and a fully custom-styled dropdown instead.
function PicklistValueSelect({ id, value, onChange, options, placeholder }) {
  const [filterText, setFilterText] = useState("");
  const hasUnlistedValue = value && !options.some((option) => option.value === value);
  const showFilter = options.length > FILTER_THRESHOLD;
  const filteredOptions = showFilter && filterText
    ? options.filter((option) => option.value.toLowerCase().includes(filterText.toLowerCase()))
    : options;

  return (
    <Dropdown onToggle={(isOpen) => isOpen && setFilterText("")}>
      <Dropdown.Toggle
        id={id}
        as="button"
        type="button"
        bsPrefix="form-select"
        className="text-start w-100"
      >
        {value || <span className="text-muted">{placeholder || "Select a value"}</span>}
      </Dropdown.Toggle>
      <Dropdown.Menu
        as={PortalDropdownMenu}
        popperConfig={{ strategy: "fixed", modifiers: [sameWidthModifier] }}
        className="shadow-lg p-0"
      >
        {showFilter && (
          <div className="p-2 border-bottom bg-white" style={{ position: "sticky", top: 0 }}>
            <Form.Control
              size="sm"
              autoFocus
              placeholder="Type to filter..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
          {hasUnlistedValue && !filterText && (
            <Dropdown.Item active onClick={() => onChange(value)} className="fst-italic">
              {value} <span className="small">(not in list)</span>
            </Dropdown.Item>
          )}
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-muted small">No matches</div>
          ) : (
            filteredOptions.map((option) => (
              <Dropdown.Item
                key={option.picklistValueId || option.value}
                active={option.value === value}
                onClick={() => onChange(option.value)}
              >
                {option.value}
              </Dropdown.Item>
            ))
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default PicklistValueSelect;
